import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Color from 'core/colors';
import styled from 'styled-components';

interface SupportLinkButtonProps {
  url: string;
  type: 'button';
  title?: string;
}

interface SupportLinkLinkProps {
  url: string;
  type: 'link';
  title: string; // Title is required for link type
}

// Union type for the component props
type SupportLinkProps = SupportLinkButtonProps | SupportLinkLinkProps;

// Styled button with hover effects
const StyledButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: none;
  color: ${Color.reportGray};
  padding: 6px 10px;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  vertical-align: middle;

  &:hover {
    background-color: ${Color.veryLightGray};
    color: ${Color.primaryGreen};
    box-shadow: 0 1px 4px ${Color.hexToRGB(Color.reportGray, 0.3)};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${Color.hexToRGB(Color.primaryGreen, 0.3)};
  }

  svg {
    font-size: 1.1em;
  }
`;

// Styled link with hover effects
const StyledLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${Color.primaryGreen};
  text-decoration: none;
  padding: 6px 2px;
  font-weight: 500;
  transition: all 0.2s ease;
  position: relative;
  vertical-align: middle;

  &:after {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    bottom: 0;
    left: 0;
    background-color: ${Color.primaryGreen};
    transition: width 0.3s ease;
  }

  &:hover {
    color: ${Color.darkGreen};

    &:after {
      width: 100%;
    }
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${Color.hexToRGB(Color.primaryGreen, 0.3)};
  }
`;

export const SupportLink = ({ url, type = 'button', title }: SupportLinkProps) => {
  const goToSupportArticle = () => {
    window.open(url, '_blank');
  };

  return type === 'button' ? (
    <StyledButton onClick={goToSupportArticle} aria-label={title ?? 'Support'}>
      <FontAwesomeIcon icon={faQuestionCircle} />
      {title && <span>{title}</span>}
    </StyledButton>
  ) : (
    <StyledLink href={url} target="_blank" rel="noreferrer">
      {title}
    </StyledLink>
  );
};
